import { useEffect, useRef, useState } from "react";
import { endpoints } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bot, Send, User, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";

export default function ChatPanel({ projectId }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

useEffect(() => {
  const load = async () => {
    if (!projectId) return;
    const { data } = await endpoints.messages(projectId);
    setMessages(data);
  };

  load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [projectId]); // Clean and perfectly valid!

useEffect(() => {
  if (scrollRef.current) {
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }
}, [messages, sending]);

  const send = async () => {
    if (!text.trim() || sending) return;
    const msg = text.trim();
    setText("");
    setSending(true);
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: msg }]);
    try {
      await endpoints.sendMessage(projectId, msg);
      await load();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full border-l border-border bg-card/40" data-testid="chat-panel">
      <div className="h-16 flex items-center gap-2 px-5 border-b border-border shrink-0">
        <div className="w-7 h-7 grid place-items-center bg-accent/15 text-accent rounded-sm">
          <Sparkles className="w-3.5 h-3.5" />
        </div>
        <div>
          <div className="text-sm font-semibold leading-none">Agent Chat</div>
          <div className="label-mono mt-1">MLFORGE · STAFF ML</div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-xs text-muted-foreground leading-relaxed">
            <p className="mb-2">Ask the agent anything about this project:</p>
            <ul className="space-y-1">
              <li>· Why was column X dropped?</li>
              <li>· Which model won and why?</li>
              <li>· Summarise the evaluation.</li>
            </ul>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} data-testid={`chat-msg-${m.role}`} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <div className={`w-6 h-6 shrink-0 rounded-sm grid place-items-center text-xs ${
              m.role === "user" ? "bg-secondary" : "bg-accent/15 text-accent"
            }`}>
              {m.role === "user" ? <User className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
            </div>
            <div className={`text-xs leading-relaxed max-w-[85%] rounded-sm px-3 py-2 border ${
              m.role === "user" ? "border-border bg-secondary" : "border-border bg-background"
            }`}>
              <div className="prose prose-invert prose-sm max-w-none prose-p:my-1 prose-code:text-accent">
                <ReactMarkdown>{m.content}</ReactMarkdown>
              </div>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex gap-2 items-center text-xs text-muted-foreground">
            <div className="w-6 h-6 rounded-sm bg-accent/15 text-accent grid place-items-center">
              <Bot className="w-3 h-3" />
            </div>
            <span className="animate-pulse">Thinking…</span>
          </div>
        )}
      </div>

      <div className="p-3 border-t border-border shrink-0">
        <div className="flex gap-2">
          <Input
            data-testid="chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask the ML agent…"
            className="text-sm bg-background"
          />
          <Button data-testid="chat-send" onClick={send} disabled={sending || !text.trim()} size="icon" className="bg-accent text-accent-foreground hover:bg-accent/90">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
